import React, { useState } from 'react';
import { useEntityList } from '@/hooks/useEntityList';
import { exportEntityCsv } from '@/lib/csv';
import { mapMember } from '../members';
import { useUser } from '@/contexts/UserContext';
import { SectionLayout } from '@/components/section-layout';
import { Trash2, Download, Upload } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { GenericTag } from '@/styles/GenericTag';
import { trackVendorCreated, trackVendorUpdated, trackVendorDeleted, trackButtonClick, trackFormSubmission } from '@/lib/posthog-events';
import { getApiUrl } from '@/lib/api-config';
import { GenericTable, GenericTableColumn } from '@/styles/GenericTable';
import { NewItemButton, SearchButton, ThreeDotMenuButton } from '@/styles/StandardButtons';
import { VendorForm } from './components/VendorForm';
import { FloatingAlert } from '@/components/ui/floating-alert';
import type { InvitationNotice } from '../commercial-invitation';
import { createVendorUser, updateVendorUser } from './store';

interface Vendor {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company_name: string;
  address: string;
  city: string;
  state: string;
  country: string;
  zip_code: string;
  is_active: boolean;
  notes: string;
  created_at: string;
  attachment_count?: number;
  cost_code_ids?: string[];
}


export function VendorsPage() {
  const { companyId, user } = useUser();
  // The list: ONE server-driven query (search + sort + page) over the company's vendors -- the database searches, sorts and pages
  // everyone the user may see; each vendor's document counter comes with the row (hooks/useEntityList.ts).
  const list = useEntityList<any, Vendor>({
    entityType: 'vendor_member',
    enabled: !!companyId,
    mode: 'pages',
    includeCounts: true,
    defaultSort: { columnKey: 'first_name', direction: 'asc', sortKey: 'name' },
    mapRow: mapMember,
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [invitationNotice, setInvitationNotice] = useState<InvitationNotice | null>(null);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const handleNewVendor = () => {
    trackButtonClick('new_vendor_button', { context: 'vendor_management' });
    setEditingVendor(null);
    setIsDialogOpen(true);
  };

  const handleEditVendor = async (vendor: Vendor) => {
    trackButtonClick('edit_vendor_button', { 
      context: 'vendor_management',
      vendor_id: vendor.id,
      vendor_name: `${vendor.first_name} ${vendor.last_name}`
    });
    setEditingVendor(vendor);
    setIsDialogOpen(true);
  };


  const parseErrorMessage = (error: any): string => {
    // If it's already a user-friendly string, return it
    if (typeof error === 'string') {
      return error;
    }

    // Check for error object with message
    // API returns { success: false, error: "...", details: "...", code: "..." }
    // When thrown as Error, it becomes { message: "...", ... }
    // Supabase errors can be nested, so check both top-level and nested error objects
    const errorMessage = error?.message || error?.error || '';
    const errorDetails = error?.details || error?.error?.details || '';
    const errorCode = error?.code || error?.error?.code || '';

    // Handle duplicate email errors
    if (
      errorMessage.includes('email') && 
      (errorMessage.includes('unique') || errorMessage.includes('duplicate') || errorCode === '23505')
    ) {
      return 'This email address is already in use. Please use a different email.';
    }

    // Handle unique constraint violations
    if (errorCode === '23505') {
      const detailsStr = String(errorDetails || '').toLowerCase();
      if (detailsStr.includes('email') || detailsStr.includes('users_email_unique')) {
        return 'This email address is already in use. Please use a different email.';
      }
      if (errorDetails?.includes('user_id') || errorDetails?.includes('company_id')) {
        return 'This vendor is already associated with this company.';
      }
      return 'This record already exists. Please check for duplicates.';
    }

    // Handle not-null constraint violations
    if (errorCode === '23502') {
      const fieldMatch = errorDetails?.match(/column "(\w+)" of relation/);
      if (fieldMatch) {
        const fieldName = fieldMatch[1];
        const friendlyName = fieldName
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (l: string) => l.toUpperCase());
        return `${friendlyName} is required.`;
      }
      return 'A required field is missing. Please check all required fields.';
    }

    // Handle foreign key violations
    if (errorCode === '23503') {
      return 'Invalid reference. Please check that all related records exist.';
    }

    // Handle check constraint violations
    if (errorCode === '23514') {
      if (errorMessage.includes('email')) {
        return 'Please enter a valid email address.';
      }
      if (errorMessage.includes('status') || errorMessage.includes('role')) {
        return 'Invalid status or role value. Please select a valid option.';
      }
      return 'Invalid data format. Please check your input.';
    }

    // Handle specific error messages from API
    if (errorMessage.includes('user_id and company_id are required')) {
      return 'Session expired. Please refresh the page and try again.';
    }

    if (errorMessage.includes('Failed to create user') || errorMessage.includes('Failed to update user')) {
      const detailsStr = String(errorDetails || '').toLowerCase();
      if (detailsStr.includes('email') || detailsStr.includes('users_email_unique') || 
          detailsStr.includes('duplicate key') || detailsStr.includes('unique constraint')) {
        return 'Failed to save vendor. This email address is already in use. Please use a different email.';
      }
      return 'Failed to save vendor. Please check all fields and try again.';
    }

    // Handle generic "Failed to create user" or "Failed to update user" messages - check if it's a duplicate email
    if (errorMessage.includes('Failed to create') || errorMessage.includes('Failed to update')) {
      const detailsStr = String(errorDetails || '').toLowerCase();
      if (detailsStr.includes('email') || detailsStr.includes('users_email_unique') || 
          detailsStr.includes('duplicate key') || detailsStr.includes('unique constraint')) {
        if (detailsStr.includes('email') || detailsStr.includes('users_email_unique')) {
          return 'Failed to save vendor. This email address is already in use. Please use a different email.';
        }
      }
    }

    // Return the original error message if we can't parse it
    return errorMessage || 'An unexpected error occurred. Please try again.';
  };

  const handleSaveVendor = async (formData: any): Promise<string | null> => {
    try {
      // 1. The acting user and the company come from the session (the server re-derives both)
      const userId = user?.id;
      if (!userId) throw new Error("User not logged in");
      if (!companyId) throw new Error("No active company found for this user");

      // 3. Insert or update vendor in users table
      const userData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        company_name: formData.company_name,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        zip_code: formData.zip_code,
        // phase53_drop_obsolete_user_columns.sql (2026-09-09): users.status
        // used to be written here too, but nothing ever read it (confirmed
        // dead) -- `user_company_relation.is_active` remains the real
        // source of truth for active/inactive.
        notes: formData.notes,
        cost_code_ids: formData.cost_code_ids ?? [],
      };

      let vendorUserId: string;

      if (editingVendor) {
        try {
          await updateVendorUser(editingVendor.id, { ...userData, is_active: formData.is_active });
        } catch (updateError) {
          throw new Error(parseErrorMessage(updateError));
        }
        vendorUserId = editingVendor.id;

        // Track successful vendor update with PostHog
        trackVendorUpdated({
          vendor_id: editingVendor.id,
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
          company_name: formData.company_name,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          country: formData.country,
          zip_code: formData.zip_code,
          is_active: formData.is_active,
          company_id: companyId,
          updated_by: userId
        });

        // Track successful form submission
        trackFormSubmission({
          form_type: 'vendor_update',
          success: true
        });
      } else {
        // Create the vendor (person + role in this company)
        const created = await createVendorUser(companyId, userId, { ...userData, is_active: formData.is_active });
        vendorUserId = created.id;

        // Track successful vendor creation with PostHog
        trackVendorCreated({
          vendor_id: vendorUserId,
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
          company_name: formData.company_name,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          country: formData.country,
          zip_code: formData.zip_code,
          is_active: formData.is_active,
          company_id: companyId,
          created_by: userId
        });

        // Track successful form submission
        trackFormSubmission({
          form_type: 'vendor_creation',
          success: true
        });
      }

      // Save documents if any were added/edited in the form
      // Note: Documents are saved in VendorForm after vendor is created/updated
      // This ensures the vendor ID exists before documents are uploaded

      // Reset form + refresh vendors
      setIsDialogOpen(false);
      setEditingVendor(null);
      list.reload();

      // Return the vendor ID so documents can be saved
      return vendorUserId;

    } catch (error: any) {
      console.error("Error saving vendor:", error);
      
      // Parse error message for better user experience
      const userFriendlyError = parseErrorMessage(error);
      
      // Track failed form submission with PostHog
      trackFormSubmission({
        form_type: editingVendor ? 'vendor_update' : 'vendor_creation',
        success: false,
        error_message: userFriendlyError
      });
      
      throw error; // Re-throw so VendorForm can handle it
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one vendor to delete.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedRows.length} vendor(s)? This action cannot be undone.`)) {
      return;
    }

    if (!companyId || !user?.id) {
      alert('Please log in again to delete vendors.');
      return;
    }

    let successCount = 0;
    let errorCount = 0;

    for (const vendorId of selectedRows) {
    try {
      // Soft delete via soft_delete_user() (see backend/migrations/
      // phase33_user_and_company_soft_delete.sql): one atomic call closes
      // the user_company_relation row AND stamps users.deleted_at/
      // deleted_by. Replaces the previous two-step PUT flow, which
      // targeted a PUT /api/entities/user_company_relation/:id route that
      // has never existed in this backend (only POST is mounted) and a
      // users.status column nothing ever reads -- confirmed dead, neither
      // step actually worked.
      const deleteResponse = await fetch(getApiUrl(`api/entities/user/${vendorId}`), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!deleteResponse.ok) {
        const errorData = await deleteResponse.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete vendor');
      }

      // Track successful vendor deletion with PostHog
      trackVendorDeleted({
          vendor_id: vendorId,
        company_id: companyId,
          deleted_by: user.id
      });
      
        successCount++;
    } catch (error) {
      console.error('Error soft-deleting vendor:', error);
        errorCount++;
    }
    }

    list.reload();
    setSelectedRows([]);

    if (errorCount > 0) {
      alert(`Deleted ${successCount} vendor(s). ${errorCount} failed.`);
    } else {
      alert(`Successfully deleted ${successCount} vendor(s).`);
    }
  };

  // Export to CSV: the vendors selected in the table, or -- with nothing selected -- EVERY vendor the current search matches,
  // read by the backend (not only the page that is showing).
  const handleExportSelected = () => exportEntityCsv(list.exportRows, selectedRows, 'vendors', 'vendors');

  // Helper functions for formatting
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Map active/inactive to GenericTag status types
  const mapActiveStatusToTag = (isActive: boolean): 'active' | 'inactive' => {
    return isActive ? 'active' : 'inactive';
  };

  // Column definitions for GenericTable
  const columns: GenericTableColumn<Vendor>[] = [
    {
      key: 'first_name',
      header: 'Name',
      width: '10rem',
      sortable: true,
      sortKey: 'name',
      render: (row) => (
        <span className="font-medium">
          {row.first_name} {row.last_name}
        </span>
      ),
    },
    {
      key: 'company_name',
      header: 'Company',
      width: '10rem',
      sortable: true,
      sortKey: 'company_name',
      render: (row) => row.company_name || '-',
    },
    {
      key: 'email',
      header: 'Contact',
      width: '8rem',
      sortable: true,
      sortKey: 'email',
      render: (row) => (
        <div>
          <div>{row.email}</div>
          {row.phone && <div className="text-sm text-gray-500">{row.phone}</div>}
        </div>
      ),
    },
    {
      key: 'city',
      header: 'Location',
      width: '8rem',
      sortable: true,
      sortKey: 'city',
      render: (row) => row.city && row.state ? `${row.city}, ${row.state}` : '-',
    },
    {
      key: 'is_active',
      header: 'Status',
      width: '6.5rem',
      sortable: true,
      sortKey: 'is_active',
      render: (row) => (
        <GenericTag 
          type="status" 
          value={mapActiveStatusToTag(row.is_active)}
        />
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      width: '5rem',
      sortable: true,
      sortKey: 'created_at',
      render: (row) => formatDate(row.created_at),
    },
    {
      key: 'attachment_count',
      header: 'Attachments',
      width: '6.25rem',
      align: 'center',
      sortable: true,
      sortKey: 'attachment_count',
      render: (row) => (
        <span className="font-medium">{row.attachment_count || 0}</span>
      ),
    },
  ];

  // Menu items for ThreeDotMenuButton
  const menuItems = [
    {
      label: 'Import',
      icon: <Upload className="w-4 h-4" />,
      onClick: () => {},
    },
    {
      label: 'Export',
      icon: <Download className="w-4 h-4" />,
      onClick: handleExportSelected,
    },
    {
      label: 'Delete',
      icon: <Trash2 className="w-4 h-4" />,
      onClick: handleDeleteSelected,
      destructive: true,
    },
  ];

  // Don't block page - let table show with skeleton rows

  return (
    <SectionLayout title="Vendors">
      <div className="space-y-6">
        {/* Action Buttons */}
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-2">
            <NewItemButton
              label="New"
              onClick={handleNewVendor}
            />
            <SearchButton
              onSearch={list.setSearchInput}
              placeholder="Search"
            />
            <ThreeDotMenuButton items={menuItems} />
          </div>
        </div>

        {/* Generic Table */}
        <GenericTable
          columns={columns}
          {...list.tableProps}
          rowKey={(row) => row.id}
          scrollContainerMaxWidth="calc(100vw - 19.2rem)"
          minTableWidth={1200}
          onRowClick={handleEditVendor}
          selectable={true}
          selectedKeys={selectedRows}
          onSelectionChange={setSelectedRows}
          emptyState="No vendors found"
        />

        {/* Add/Edit Vendor Form */}
        <VendorForm
          isOpen={isDialogOpen}
          vendor={editingVendor}
          onClose={() => {
            setIsDialogOpen(false);
            setEditingVendor(null);
          }}
          onSave={handleSaveVendor}
          onInvitation={setInvitationNotice}
        />
        {invitationNotice && <FloatingAlert title={invitationNotice.title} description={invitationNotice.description} variant={invitationNotice.variant} onDismiss={() => setInvitationNotice(null)} />}

      </div>
    </SectionLayout>
  );
} 